// =============================================================================
// AssetDocuments — the documents on an asset's file (mig 510, 5 Nov 2026)
// =============================================================================
// James: "Where do we add the PDFs like safety cert or rego docs?" Same table
// and bucket as the photos (asset_photos / asset-documents); a document is a
// row with a doc_kind. Rego, safety cert, insurance, service, purchase, other,
// each with an optional expiry so the file says when it runs out.
// =============================================================================
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Upload, Trash2, ExternalLink, AlertTriangle, CalendarClock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button, Spinner, Pill } from "@/components/ui";
import type { AssetKind } from "./AssetPhotoGallery";

const BUCKET = "asset-documents";
const MAX_BYTES = 25 * 1024 * 1024;
const KINDS: { code: string; label: string; expires: boolean }[] = [
  { code: "rego",        label: "Registration",            expires: true },
  { code: "safety_cert", label: "Safety certificate",      expires: true },
  { code: "insurance",   label: "Insurance",               expires: true },
  { code: "service",     label: "Service / inspection",    expires: false },
  { code: "purchase",    label: "Purchase / finance",      expires: false },
  { code: "other",       label: "Other",                   expires: false },
];

type DocRow = { photo_id: string; storage_path: string; filename: string | null; caption: string | null; mime_type: string | null; size_bytes: number | null; uploaded_at: string; doc_kind: string; expires_on: string | null };

export function AssetDocuments({ assetKind, assetId, canEdit }: { assetKind: AssetKind; assetId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState("rego");
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["asset_docs", assetKind, assetId], enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("asset_photos").select("photo_id, storage_path, filename, caption, mime_type, size_bytes, uploaded_at, doc_kind, expires_on")
        .eq("asset_kind", assetKind).eq("asset_id", assetId).not("doc_kind", "is", null).order("doc_kind").order("uploaded_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DocRow[];
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BYTES) throw new Error(`Too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 25 MB).`);
      const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const path = `${assetKind}/${assetId}/docs/${kind}-${stamp}.${ext}`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
      if (upErr) throw upErr;
      const { error } = await (supabase as any).from("asset_photos").insert({ asset_kind: assetKind, asset_id: assetId, storage_path: path, filename: file.name, mime_type: file.type || "application/pdf", size_bytes: file.size, doc_kind: kind, expires_on: expires || null, caption: note.trim() || null });
      if (error) { await supabase.storage.from(BUCKET).remove([path]).catch(() => {}); throw error; }
    },
    onSuccess: () => { setErr(null); setNote(""); setExpires(""); qc.invalidateQueries({ queryKey: ["asset_docs", assetKind, assetId] }); },
    onError: (e: any) => setErr(e?.message ?? "Upload failed"),
  });
  const remove = useMutation({
    mutationFn: async (d: DocRow) => { await supabase.storage.from(BUCKET).remove([d.storage_path]).catch(() => {}); const { error } = await supabase.from("asset_photos").delete().eq("photo_id", d.photo_id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["asset_docs", assetKind, assetId] }),
  });
  async function open(d: DocRow) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(d.storage_path, 600);
    if (error || !data?.signedUrl) { window.alert("Couldn't open that file."); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  }
  const kindExpires = KINDS.find(k => k.code === kind)?.expires ?? false;
  const today = new Date().toISOString().slice(0, 10);
  const daysTo = (d: string) => Math.round((new Date(d + "T00:00:00").getTime() - new Date(today + "T00:00:00").getTime()) / 86400000);

  return (
    <div className="rounded-xl border border-line bg-ink-card">
      <div className="px-5 py-4 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5"><FileText size={13}/>Documents <span className="text-text-dim normal-case tracking-normal">({docs.length})</span></div>
          <div className="text-[11px] text-text-dim mt-0.5">Rego, safety certificate, insurance, service reports — the paper on this vehicle, with when it runs out.</div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <select value={kind} onChange={e => setKind(e.target.value)} className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">{KINDS.map(k => <option key={k.code} value={k.code}>{k.label}</option>)}</select>
            {kindExpires && <input type="date" value={expires} onChange={e => setExpires(e.target.value)} className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" title="Expires on"/>}
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="note (optional)" className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text w-[150px]"/>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }}/>
            <Button size="sm" icon={Upload} disabled={upload.isPending} onClick={() => fileRef.current?.click()}>{upload.isPending ? "Uploading…" : "Add document"}</Button>
          </div>
        )}
      </div>
      {err && <div className="px-5 pb-3 text-[12px] text-danger flex items-center gap-1"><AlertTriangle size={12}/>{err}</div>}
      {isLoading ? <div className="px-5 pb-5"><Spinner/></div> : docs.length === 0 ? (
        <div className="px-5 pb-5 text-[12px] text-text-muted">No documents yet{canEdit ? " — pick the kind, set when it expires, Add document." : "."}</div>
      ) : (
        <div className="border-t border-line">
          {docs.map(d => {
            const k = KINDS.find(x => x.code === d.doc_kind);
            const dt = d.expires_on ? daysTo(d.expires_on) : null;
            return (
              <div key={d.photo_id} className="px-5 py-2.5 border-b border-line/60 flex items-center gap-3">
                <FileText size={16} className="text-text-muted flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] text-text">{k?.label ?? d.doc_kind}</span>
                    {dt != null && (dt < 0 ? <Pill label={`expired ${-dt} d ago`} small tone="critical"/> : dt <= 30 ? <Pill label={`${dt} d left`} small tone="warning"/> : <span className="text-[11px] text-text-muted inline-flex items-center gap-1"><CalendarClock size={10}/>to {new Date(d.expires_on!).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>)}
                  </div>
                  <div className="text-[11px] text-text-muted truncate">{d.filename ?? d.storage_path.split("/").pop()}{d.caption ? ` · ${d.caption}` : ""} · {new Date(d.uploaded_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}{d.size_bytes ? ` · ${(d.size_bytes / 1024 / 1024).toFixed(1)} MB` : ""}</div>
                </div>
                <button type="button" onClick={() => open(d)} className="text-accent hover:underline text-[12px] inline-flex items-center gap-1"><ExternalLink size={12}/>open</button>
                {canEdit && <button type="button" onClick={() => { if (window.confirm(`Delete ${k?.label ?? "this document"} (${d.filename ?? ""})?`)) remove.mutate(d); }} className="text-text-muted hover:text-danger p-1"><Trash2 size={13}/></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
